import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router';
import type { ReactNode } from 'react';
import { NotFound } from '../../ui';
import { Loading } from '../admin/AdminLayout';
import { getMyClientRow, getMyInstance } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { useInstance } from '../instance/InstanceProvider';
import { useSession } from '../session/SessionProvider';
import { mayEnter, useChoices } from './consentChoices';

/**
 * Layer two of the three that keep one instance out of another (design.md F5).
 *
 * A signed-in person whose instance is not this slug's instance gets **not
 * found**, never a redirect to their own. A redirect would confirm that the
 * other instance exists and that they are on the wrong side of it, and
 * `access-and-consent` requires no hint that the content exists. A coach on a
 * client surface gets the same answer, for the same reason.
 *
 * "Their own instance" is read from the database, not from the URL:
 * `getMyInstance` carries no filter at all, so the row that comes back is
 * whichever one the caller's own policy allows — and if its slug is not the
 * slug in the address, this is not their surface.
 *
 * A visitor with no session is a different case: nothing has been revealed to
 * them, and the instance's front door is public anyway, so they are sent there
 * rather than told off.
 *
 * None of this is the security boundary. Layer three is: no query on these
 * pages passes a tenant filter, so row-level security returns nothing for
 * another instance's rows even if this component were deleted.
 *
 * ## The acknowledgement gate, and why it is here and nowhere else
 *
 * `client-me` requires that nothing — no plan, no session, no note — is shown
 * before the client has been told what this is and has agreed. That is one rule,
 * so it has one enforcement point, and this is it: every client screen is
 * already inside this component, so a screen added later inherits the gate
 * without anybody remembering to add it. A second opinion somewhere else is
 * exactly how a surface ends up reachable before the acknowledgement.
 *
 * `beforeAck` is for the acknowledgement screen itself, which needs the three
 * tenancy layers and must not be gated by the thing it exists to collect.
 *
 * **What "has acknowledged" means today.** `client.ack_at` if the row carries
 * one, otherwise this device's own record — because there is no
 * `client_update_self` policy, so the client cannot write the column and this
 * change adds no migration (`./consentChoices.ts`). The consequence is real and
 * is stated on the screen: another phone asks again.
 */
export function ClientGuard({
  children,
  beforeAck = false,
}: {
  children: ReactNode;
  /** The acknowledgement screen itself, which cannot be behind its own gate. */
  beforeAck?: boolean;
}) {
  const instance = useInstance();
  const { status, profile, profileLoaded } = useSession();
  const choices = useChoices();

  const signedInClient = status === 'authenticated' && profile?.role === 'client';

  const mine = useQuery({
    queryKey: ['my-instance'],
    queryFn: () => getMyInstance(supabase),
    enabled: signedInClient,
  });

  const me = useQuery({
    queryKey: ['my-client-row'],
    queryFn: () => getMyClientRow(supabase),
    enabled: signedInClient && !beforeAck,
  });

  if (status === 'loading' || (status === 'authenticated' && !profileLoaded)) return <Loading />;

  if (status === 'anonymous') return <Navigate to={`/c/${instance.slug}`} replace />;

  if (profile?.role !== 'client') return <NotFound />;

  if (mine.isPending) return <Loading />;
  if (mine.data?.slug !== instance.slug) return <NotFound />;

  if (!beforeAck) {
    if (me.isPending) return <Loading />;
    if (!mayEnter(me.data, choices)) {
      return <Navigate to={`/c/${instance.slug}/start`} replace />;
    }
  }

  return <>{children}</>;
}
